import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useWidthContext } from '../../providers/Width';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { AcquisitionFlow, getAcquisitionFlow, getProgramData } from '../../utils/acquisition-utils';
import { flowDataModule } from '../../utils/flowDataModule';

import './PageRibbon.css';
import { requestContentFragment } from '../../utils/contentFragmentUtil';
import SessionStorage from '../../utils/SessionStorage';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { usePersonalizationCustomizationContext } from '../../providers/PersonalizationCustomizationProvider/PersonalizationCustomizationProvider';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider'; 

import { PageRibbon as C1B_PageRibbon } from '../../importCL';
interface IPageRibbonProps extends MappedComponentProperties {
    desktopText?: string;
    mobileText?: string;
    headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p';
    preApprovedMACFPath?: string;
}

export const PageRibbonEditConfig = {
    emptyLabel: 'PageRibbon',
    isEmpty: (props: PropsWithChildren<IPageRibbonProps>) => {
        return !props?.desktopText;
    },
};

const PageRibbon = ({
    desktopText: propDesktopText,
    mobileText: propMobileText,
    headingTag,
    preApprovedMACFPath,
}: PropsWithChildren<IPageRibbonProps>): JSX.Element => {
    const { width } = useWidthContext();
    const { RenderPushdown } = usePushdownContext();
    const [desktopText, setDesktopText] = useState<string>(propDesktopText || '');
    const [mobileText, setMobileText] = useState<string>(propMobileText || '');

    const personalizationData = JSON.parse(PathUtils.getMetaPropertyValue('AcquisitionPersonalization') || '{}');
    const dataEntryRibbonOverrides = JSON.parse(PathUtils.getMetaPropertyValue('ribbonOverrides') || '{}');
    const paSourceIdOverride = JSON.parse(PathUtils.getMetaPropertyValue('sourceIdOverrides') || '{}');
    const breakpointForText = paSourceIdOverride.mobileText !== undefined ? 599 : 768;
 
    const { isApplicationInfoRecieved, isPartnerHostedGetComplete } = useAcquisitionContext();
    const { activePersonalizations, setActivePersonalizations } = usePersonalizationCustomizationContext();

    const getFirstName = async  () => {
        return (await flowDataModule.getFromSession())?.FirstName || await SessionStorage.getCipher('personalInfo', true).then(personalInfo => personalInfo.FirstName).catch(() => null) || '';
    };

    const handlePersonalisation = async () => {
        const params = new URLSearchParams(window.location.search);
        const name = await getFirstName();
        const flow = getAcquisitionFlow();
        const isObjectEmpty = Object.keys(dataEntryRibbonOverrides).length === 0;
        const overrideDataEntryThisFlow = !isObjectEmpty && dataEntryRibbonOverrides[getAcquisitionFlow()];

        if (paSourceIdOverride.mobileText !== undefined) {
            setMobileText(paSourceIdOverride.mobileText);
        }

        if ((!name ||
            name === '' ||
            !personalizationData?.usePersonalization ||
            personalizationData.ignoreSourceIds?.includes(params.get('C1BSourceID'))) &&
            !window.location.pathname.includes('data-entry')
        ) {
            setDesktopText(propDesktopText?.replace(/{CustomPersonalization}/g, ''));
            setMobileText(propMobileText?.replace(/{CustomPersonalization}/g, ''));
            return;
        }

        if (flow === AcquisitionFlow.PHA && !isPartnerHostedGetComplete) {
            return;
        }

        if (window.location.pathname.includes('data-entry') && overrideDataEntryThisFlow) {
            setDesktopText(dataEntryRibbonOverrides.ribbonText);
            setMobileText(dataEntryRibbonOverrides.ribbonText);
            return;
        }

        const overrideMap: {
            [key in AcquisitionFlow]?: string | null
        } = {
            skipPQ: PathUtils.getMetaPropertyValue('SkipPqRibbonContent'),
            preQual: JSON.parse(PathUtils.getMetaPropertyValue('PQMappingContent') || '{}')?.ribbonText,
        };

        if (overrideMap[flow]) {
            setDesktopText(overrideMap[flow]!);
            setMobileText(overrideMap[flow]!);

        } else if (
            activePersonalizations.OfferHeadline || 
            propDesktopText?.includes('{CustomPersonalization}') || 
            propMobileText?.includes('{CustomPersonalization}')
        ) {
            setActivePersonalizations((prev) => ({ ...prev, PageRibbon: !activePersonalizations.OfferHeadline }));
            setDesktopText(propDesktopText?.replace(/{CustomPersonalization}/g, activePersonalizations.OfferHeadline ? '': name));
            setMobileText(propMobileText?.replace(/{CustomPersonalization}/g, activePersonalizations.OfferHeadline ? '': name));

        } else if (personalizationData?.usePersonalization) {
            const fetchPreApprovedMultipleAccountContentFragment = async (path: string) => {
                try {
                    const response = await requestContentFragment({ path });
                    if (response.data[0]) {
                        let { desktopRibbonText, mobileRibbonText } = response.data[0];
                        desktopRibbonText = desktopRibbonText.replace(/{Firstname}/g, name);
                        mobileRibbonText = mobileRibbonText.replace(/{Firstname}/g, name);
                        setDesktopText(desktopRibbonText);
                        setMobileText(mobileRibbonText);
                    }
                } catch (err) {
                    console.error('Error fetching CF Data: ' + err);
                }
            };

            let nextText = '';
            switch (flow) {
                case AcquisitionFlow.PA:
                    if (preApprovedMACFPath && (await getProgramData()).IsMultipleAccountProgram) {
                        await fetchPreApprovedMultipleAccountContentFragment(preApprovedMACFPath);
                        return;
                    }
                    nextText = '{Firstname}, You\'re Pre-Approved!';

                    break;

                case AcquisitionFlow.PHA:
                case AcquisitionFlow.PQ:
                case AcquisitionFlow.PqOfOne:
                    nextText = '{Firstname}, You\'re Pre-Qualified!';
                    break;
            }

            if (nextText) {
                nextText = nextText.replace(/{Firstname}/g, name);
                setDesktopText(nextText);
                setMobileText(nextText);
            }
        }
    };

    useEffect(() => {
        handlePersonalisation();
    }, [isApplicationInfoRecieved, isPartnerHostedGetComplete, activePersonalizations.OfferHeadline]);

    return (
        desktopText ?
            <>
                <C1B_PageRibbon className='Ribbon'
                    tag={headingTag || 'h1'}>
                    {transformHTMLParserLinks(
                        !mobileText || width > breakpointForText
                            ? desktopText
                            : mobileText,
                    )}
                </C1B_PageRibbon>
                <RenderPushdown/>
            </>
            :
            <></>
    );
};

export default PageRibbon;
